import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  Deal,
  PipelineStat,
  DealsFilters,
  CreateDealPayload,
  UpdateDealPayload,
  DealScore,
} from '../types';

export interface DealsResponse {
  deals: Deal[];
  pipelineStats: PipelineStat[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useDeals(filters: DealsFilters = {}) {
  return useQuery({
    queryKey: ['deals', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.stage) params.set('stage', filters.stage);
      if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
      const qs = params.toString();
      const { data } = await api.get<DealsResponse>(`/api/deals${qs ? `?${qs}` : ''}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * AI win-probability scores keyed by dealId. The endpoint returns scores for
 * all open deals globally; callers only ever read scores for deals already in
 * their own visible list, so no cross-user data is surfaced.
 */
export function useDealScores(enabled = true) {
  return useQuery({
    queryKey: ['deals', 'ai-scores'],
    queryFn: async () => {
      const { data } = await api.get<{ deals: DealScore[] }>('/api/ai/deal-scores');
      const byId: Record<string, number> = {};
      (data.deals ?? []).forEach((d) => {
        byId[d.dealId] = d.score;
      });
      return byId;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useDealMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['deals'] });

  const createDeal = useMutation({
    mutationFn: async (payload: CreateDealPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/deals', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateDeal = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateDealPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/deals/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteDeal = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/deals/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createDeal, updateDeal, deleteDeal };
}
